/**
 * Feel (v1 ticket 37): three short, soft effects, each a sound and one short vibration pulse.
 *
 * - `brick`: a brick banked on a passed review card in the round (15).
 * - `fog`: a word's fog lifting on a passed first look.
 * - `build`: a building going up in the round's payoff; a first uses it too.
 * - `bell` and `land` (v1 ticket 40): the opening's word coming loose (a bell of a pentatonic scale, `n` its note) and
 *   its light landing in the valley (a low, soft thud).
 *
 * The opening's night ambience (`opening/ambience.ts`) has its own context in the opening chunk and reads this switch.
 *
 * Sounds are made here with Web Audio (no files, no licence), vibration is `navigator.vibrate` (Android; Safari
 * on iPhone has none, so the iPhone gets sound only). "Sounds and vibration" in home's ⋯ menu turns both off, on
 * this device only (localStorage); it never touches the listen buttons or a card's audio.
 *
 * The listen buttons play through an audio element (`tts.ts`) and must keep playing with an iPhone on silent. So
 * this module never sets `navigator.audioSession` and never touches that element: effects go through their own
 * AudioContext, which Safari mutes with the silent switch where it can. The context starts only on the learner's
 * tap in a view that plays effects (`armFeel`), and is suspended again when idle so it holds no audio session.
 *
 * Every call is fire and forget: nothing here is awaited, so no effect ever delays the next card.
 */

export type Effect = 'brick' | 'fog' | 'build' | 'bell' | 'land';

/** Per device, not per account: the switch is about this phone. */
const KEY = 'li-feel';
/** Suspend the context this long after the last effect. */
const IDLE_MS = 2000;
/** One short pulse per effect, in ms (0: none; a bell only rings). */
const PULSE: Record<Effect, number> = { brick: 12, fog: 10, build: 20, bell: 0, land: 8 };

let ctx: AudioContext | null = null;
let idle: ReturnType<typeof setTimeout> | undefined;
let noise: AudioBuffer | null = null;
/** The bells' shared echo, on the current context. */
let echo: AudioNode | null = null;

/** Whether effects are on for this device: on unless the learner turned them off. */
export function feelOn(): boolean {
  try {
    return localStorage.getItem(KEY) !== 'off';
  } catch {
    return true;
  }
}

/** The ⋯ menu's switch. Turning it off also lets the effects' audio go at once. */
export function setFeel(on: boolean): void {
  try {
    if (on) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, 'off');
  } catch { /* private mode: the switch holds for this page only */ }
  if (!on) release();
}

function release() {
  if (idle !== undefined) clearTimeout(idle);
  idle = undefined;
  const c = ctx;
  ctx = null;
  noise = null;
  echo = null;
  if (c && c.state !== 'closed') void c.close().catch(() => {});
}

/** The effects' own context, started (or woken) now; null where Web Audio is missing or effects are off. */
function context(): AudioContext | null {
  if (!feelOn()) return null;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  try {
    if (!ctx || ctx.state === 'closed') ctx = new AC({ latencyHint: 'interactive' });
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  } catch {
    return null;
  }
  return ctx;
}

/**
 * While a view that plays effects is on screen, the learner's taps may start or wake the context: Safari starts
 * audio only inside a tap. Returns the undo, for the view's disconnect.
 */
export function armFeel(): () => void {
  const wake = () => {
    if (!feelOn() || ctx?.state === 'running') return;
    context();
    rest();
  };
  document.addEventListener('pointerdown', wake, { capture: true, passive: true });
  return () => document.removeEventListener('pointerdown', wake, { capture: true });
}

/** Suspend the context once no effect has played for a while. */
function rest() {
  if (idle !== undefined) clearTimeout(idle);
  idle = setTimeout(() => {
    idle = undefined;
    if (ctx?.state === 'running') void ctx.suspend().catch(() => {});
  }, IDLE_MS);
}

/** A short vibration on its own (no sound), honouring the same switch. Never throws. */
export function pulse(pattern: number | number[]): void {
  try {
    if (feelOn()) navigator.vibrate?.(pattern);
  } catch { /* no vibration here */ }
}

/** Plays one effect now, if effects are on (`n`: a bell's note). Never throws, never waits. */
export function feel(effect: Effect, n = 0): void {
  if (!feelOn()) return;
  try {
    if (PULSE[effect]) navigator.vibrate?.(PULSE[effect]);
  } catch { /* no vibration here */ }
  try {
    const c = context();
    if (!c) return;
    SOUNDS[effect](c, c.currentTime + 0.01, n);
    rest();
  } catch { /* an effect is never worth an error */ }
}

/** The bells' echo (0.28 s, softened), made once per context. */
function echoOf(c: AudioContext): AudioNode {
  if (echo && echo.context === c) return echo;
  const d = c.createDelay(1), fb = c.createGain(), wet = c.createGain(), lp = c.createBiquadFilter(), inp = c.createGain();
  d.delayTime.value = 0.28; fb.gain.value = 0.38; wet.gain.value = 0.5; lp.type = 'lowpass'; lp.frequency.value = 2600;
  inp.connect(d); d.connect(lp); lp.connect(fb); fb.connect(d); lp.connect(wet); wet.connect(c.destination);
  return (echo = inp);
}

// -- the sounds: short and soft ------------------------------------------------------------------------------------

/** A gain that rises in `attack` s to `peak` and falls away over `decay` s, from `t`, into `to` (the speakers). */
function envelope(c: AudioContext, t: number, peak: number, attack: number, decay: number, to: AudioNode = c.destination): GainNode {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.connect(to);
  return g;
}

/** A tone from `from` Hz gliding to `to` Hz, through `out`, for `len` s. */
function tone(c: AudioContext, out: AudioNode, type: OscillatorType, t: number, from: number, to: number, len: number) {
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(from, t);
  if (to !== from) o.frequency.exponentialRampToValueAtTime(to, t + len * 0.6);
  o.connect(out);
  o.start(t);
  o.stop(t + len + 0.02);
}

function whiteNoise(c: AudioContext): AudioBuffer {
  if (noise && noise.sampleRate === c.sampleRate) return noise;
  const b = c.createBuffer(1, Math.round(c.sampleRate * 0.6), c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return (noise = b);
}

const SOUNDS: Record<Effect, (c: AudioContext, t: number, n: number) => void> = {
  /** A soft wooden tock: a low triangle that drops a little. */
  brick(c, t) {
    tone(c, envelope(c, t, 0.09, 0.004, 0.11), 'triangle', t, 520, 360, 0.12);
  },
  /** An airy breath: noise through a band that opens upward. */
  fog(c, t) {
    const src = c.createBufferSource();
    src.buffer = whiteNoise(c);
    const band = c.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 0.8;
    band.frequency.setValueAtTime(500, t);
    band.frequency.exponentialRampToValueAtTime(2600, t + 0.45);
    src.connect(band);
    band.connect(envelope(c, t, 0.05, 0.16, 0.32));
    src.start(t);
    src.stop(t + 0.5);
  },
  /** A small two-note chime, a fifth apart. */
  build(c, t) {
    tone(c, envelope(c, t, 0.06, 0.006, 0.3), 'sine', t, 660, 660, 0.32);
    tone(c, envelope(c, t + 0.09, 0.05, 0.006, 0.36), 'sine', t + 0.09, 990, 990, 0.38);
  },
  /** A word comes loose: one bell of a pentatonic scale (note `n`), hanging in the air on its echo. */
  bell(c, t, n) {
    const notes = [880, 1046.5, 1174.7, 1318.5, 1568, 1760], f = notes[((n % notes.length) + notes.length) % notes.length];
    const g = envelope(c, t, 0.05, 0.01, 1.6);
    g.connect(echoOf(c));
    tone(c, g, 'sine', t, f, f, 1.6);
    tone(c, envelope(c, t, 0.012, 0.005, 0.5), 'sine', t, f * 2.01, f * 2.01, 0.5);
  },
  /** A light lands: a low, soft thud gliding down an octave. */
  land(c, t) {
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(55, t + 0.35);
    o.connect(envelope(c, t, 0.09, 0.008, 0.35));
    o.start(t);
    o.stop(t + 0.4);
  },
};
